import React from 'react';
import { Button, Col, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Flex from '@/components/common/Flex';
import classNames from 'classnames';
import {
  useOrderRegisterMutation,
  useRePaymentMutation,
  useShowInvoiceMutation
} from '@/app/Purchase/api/ordersApi';
import { errorMessage, warningMessage } from '@/utils/toast';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import IntroCodeForm from './IntroCodeForm';
import { calculatePayableAmount } from '../../utils';
import type { OrderActionsProps } from '../../types/purchaseTypes';

function OrderActions({
  isMemberStep,
  searchParams,
  selectedTariff,
  introCodeData,
  setIsLegalModalOpen,
  agreedWithTerms,
  setIsAgreedWithTermsError,
  navigate,
  onIntroCodeSuccess,
}: OrderActionsProps) {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const [orderRegister, { isLoading: isOrderRegistering }] =
    useOrderRegisterMutation();
  const [repayment, { isLoading: isRepaymenting }] = useRePaymentMutation();
  const [showInvoice, { isLoading: isInvoiceLoading }] =
    useShowInvoiceMutation();
  const [isIntroCodeOpen, setIsIntroCodeOpen] = React.useState(false);

  const tariffSlug = searchParams.get('t') || searchParams.get('r');
  const weeklyUpdates = Number(searchParams.get('wu')) || null;

  const handleShowInvoice = () => {
    const tariffId = tariffSlug?.match(/\d+$/)?.[0];
    if (!tariffId) return;

    showInvoice({ tariff_slug: tariffId })
      .unwrap()
      .then(response => {
        if (response.code === 60002) {
          setIsLegalModalOpen(true);
          warningMessage(
            'برای چاپ پیش‌فاکتور اطلاعات لازم رو اضافه کنید.'
          );
        } else if (response.success) {
          const basePath = `/orders/invoice/${tariffId}?wu=${weeklyUpdates}`;
          const url = introCodeData?.data?.code
            ? `${basePath}&inc=${introCodeData.data.code}`
            : basePath;
          window.open(url);
        }
      })
      .catch(() => {
        errorMessage('خطا در دریافت اطلاعات فاکتور');
      });
  };

  const calculatePayableAmountLocal = () =>
    calculatePayableAmount(selectedTariff, introCodeData);

  const handleOrderSubmit = () => {
    if (!agreedWithTerms) {
      setIsAgreedWithTermsError(true);
      return;
    }

    orderRegister({
      tariff_slug: tariffSlug,
      weekly_updates: weeklyUpdates,
      introduced_code: introCodeData?.data?.code || null
    })
      .unwrap()
      .then(response => {
        if (response?.data?.need_to_pay !== true) {
          navigate(
            isMemberStep ? '/membership-steps/domains' : '/'
          );
        } else {
          repayment(isMemberStep ? 'membership' : 'prorogate')
            .unwrap()
            .then(response => {
              if (response.success) {
                location.replace(response.data);
              }
            })
            .catch(() => {
              errorMessage('خطا در انتقال به صفحه پرداخت');
            });
        }
      });
  };

  const getBackUrl = () => {
    if (isMemberStep) return '/membership-steps';
    return '/prorogate-or-upgrade/';
  };

  const getChangePlanUrl = () => {
    return isMemberStep
      ? '/membership-steps'
      : '/prorogate-or-upgrade/';
  };

  const payableAmount = calculatePayableAmountLocal();
  const buttonText =
    payableAmount === 0 ? 'تایید سفارش' : 'پرداخت';
  const isLoading = isOrderRegistering || isRepaymenting;

  return (
    <Flex
      justifyContent="between"
      className="mt-5 flex-wrap flex-column gap-3 px-md-6 bg-body-tertiary d-flex card-footer"
    >
      <Row className="g-3">
        <Col xs={6} md="auto">
          <IntroCodeForm
            isOpen={isIntroCodeOpen}
            setIsOpen={setIsIntroCodeOpen}
            planId={tariffSlug}
            orderAmount={Number(selectedTariff?.data?.amount_payable) || 0}
            onSuccess={(data) => {
              onIntroCodeSuccess?.(data);
            }}
          />
        </Col>
        <Col xs={6} md="auto">
          {!isMemberStep && (
            <Button
              disabled={isInvoiceLoading}
              onClick={handleShowInvoice}
              variant="secondary"
              className="w-100"
            >
              <span className="me-1 fs--1">
                {isInvoiceLoading ? (
                  <>
                    <Spinner
                      style={{ width: '16px', height: '16px' }}
                      className="me-1"
                    />
                    درحال بررسی
                  </>
                ) : (
                  'پیش‌فاکتور'
                )}
              </span>
            </Button>
          )}
        </Col>
        <Col xs={12} md="auto">
          <Link className="text-decoration-none w-100" to={getChangePlanUrl()}>
            <Button variant="secondary" className="w-100">
              خرید طرح
            </Button>
          </Link>
        </Col>
      </Row>

      <div className="d-flex align-items-center justify-content-end gap-3">
        <Link className="text-decoration-none" to={getBackUrl()}>
          <Button variant="secondary">قبلی</Button>
        </Link>
        <Button
          disabled={isLoading}
          variant="falcon-primary"
          className={classNames({ 'flex-1': !isMdUp })}
          onClick={handleOrderSubmit}
        >
          {isLoading ? (
            <>
              <Spinner
                style={{ width: '16px', height: '16px' }}
                className="me-1"
              />
              {isMdUp ? <>انتقال به صفحه پرداخت</> : 'انتقال'}
            </>
          ) : (
            buttonText
          )}
        </Button>
      </div>
    </Flex>
  );
};

export default OrderActions;

